import { VGridRef } from "@src/components/vGrid/VGrid";
import { eventActions, settingsActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { ManageInventoryTabEnum } from "@src/store/pricing/selectionSlice";
import { RefObject, useCallback, useRef } from "react";
import { requestAutopricerExit } from "./autopricerExitGuard";

type TSelectionMode = "intercept" | "apply" | "ignore";

interface IUseAutopricerListingSwitch {
	gridRef: RefObject<VGridRef>;
	getRows: () => Array<EventInventoryRecord>;
	getRowNodeId: (row: EventInventoryRecord) => string;
}

export const useAutopricerListingSwitch = ({ gridRef, getRows, getRowNodeId }: IUseAutopricerListingSwitch) => {
	const dispatch = useAppDispatch();
	const isAutopricerOpen = useAppSelector(
		(state) =>
			state.pricing.selection.isEditListingEnabled &&
			state.pricing.selection.manageInventoryTab === ManageInventoryTabEnum.Autopricer,
	);
	const inventoryItemIds = useAppSelector((state) => state.pricing.selection.inventoryItemIds);

	const modeRef = useRef<TSelectionMode>("intercept");
	const latestRef = useRef({ isAutopricerOpen, inventoryItemIds, getRows, getRowNodeId });
	latestRef.current = { isAutopricerOpen, inventoryItemIds, getRows, getRowNodeId };

	const runInMode = (mode: TSelectionMode, action: () => void) => {
		modeRef.current = mode;
		try {
			action();
		} finally {
			modeRef.current = "intercept";
		}
	};

	const setGridSelection = (selectedRows: Array<EventInventoryRecord>) => {
		const grid = gridRef.current;
		if (!grid) return;

		const [first, ...rest] = selectedRows.map((row) => ({ id: latestRef.current.getRowNodeId(row), isChecked: true }));
		if (!first) {
			grid.clearSelection();
			return;
		}

		grid.strictRowSet([first]);
		if (rest.length) grid.setCheckedRowIds(rest);
	};

	const openAutopricerForRow = useCallback(
		(row: EventInventoryRecord) => {
			runInMode("apply", () => setGridSelection([row]));

			if (row.Is_AutoPriced) {
				dispatch(eventActions.setInventory({ isEditListingEnabled: false }));
				dispatch(settingsActions.setAutoPricerPopup(true));
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dispatch],
	);

	const selectRowProgrammatically = useCallback((row: EventInventoryRecord) => {
		runInMode("apply", () => setGridSelection([row]));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const interceptSelection = useCallback(
		(selectedRows: Array<EventInventoryRecord>): boolean => {
			if (modeRef.current === "ignore") return true;
			if (modeRef.current === "apply") return false;

			const { isAutopricerOpen: isOpen, inventoryItemIds: currentIds, getRows: getAllRows } = latestRef.current;
			if (!isOpen) return false;

			const currentRows = getAllRows().filter((row) => currentIds.includes(row.Item_ID));
			const addedRows = selectedRows.filter((row) => !currentIds.includes(row.Item_ID));

			runInMode("ignore", () => setGridSelection(currentRows));

			if (addedRows.length !== 1) return true;

			const [target] = addedRows;
			requestAutopricerExit(() => openAutopricerForRow(target));
			return true;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[openAutopricerForRow],
	);

	return { interceptSelection, selectRowProgrammatically };
};
